import { useState } from 'react';
import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { EnvironmentalTabBar } from '../components/environmental/EnvironmentalTabBar';
import { EnvironmentalFooter } from '../components/environmental/EnvironmentalFooter';
import { RegulatoryRegisterLink } from '../components/environmental/RegulatoryRegisterLink';
import { requireOrgFeature } from '../lib/orgFeatureGate';

/**
 * SIL-472 — Environmental dashboard layout. Sibling tab pages (Overview
 * built this slice; Fleet detail/Circularity/Reports are "In development"
 * placeholders — SIL-460-adjacent follow-ups fill them in) mount under
 * `environmental/*.tsx`, matching the `financials.tsx` + `financials/*.tsx`
 * layout convention already used elsewhere in this app.
 *
 * The FY25/FY26/All year toggle is presentation-only this slice: the
 * underlying data has ~2 months of history fleet-wide (readiness doc), not
 * enough to make a per-fiscal-year filter meaningful yet. It renders so the
 * IA reads complete, and the API is intentionally NOT wired to it yet
 * rather than silently no-op filtering — a future slice adds the query
 * param once there's a fiscal year's worth of data to filter.
 *
 * The SIL-459 footer disclosure renders on EVERY tab via this shared
 * layout, never per-tab.
 *
 * SIL-540/SIL-542: Reports was promoted to its own CUSTOMER_NAV entry
 * ("ESG Reports", distinct from this layout's "Carbon footprint" title) —
 * so on `/environmental/reports` this layout steps aside and lets
 * `environmental/reports.tsx` render its own PageHeader instead, to keep
 * the Nav-Title Match rule satisfied for that promoted entry. SIL-576
 * (I2-D4) does the same for Circularity ("Circularity Outcomes") on
 * `/environmental/circularity`. Overview / Fleet detail have no nav entry
 * of their own (they're pure tabs), so they keep sharing this layout's
 * single header.
 *
 * NOTE (SIL-576): the actuals disclosure footer below (`EnvironmentalFooter`)
 * keeps rendering unconditionally on every path, including the promoted
 * reports/circularity ones — the per-tab disclosure-slot mechanism from
 * SIL-543 (carbon-planning, which REPLACES this footer) is not merged into
 * main yet. Do not duplicate that work here; when SIL-543 lands it will
 * rebase this conditional.
 *
 * SIL-1108: `orgFeature: 'sustainability'` (customerNav.ts) is
 * business-only. A direct hit on any tab under this layout fired that tab's
 * own business-only fetch before the root `OrgFeatureGate`'s (__root.tsx)
 * `useEffect`-based redirect landed — same class of bug as SIL-1102/
 * SIL-1108's other fixes. Gated here, in the layout's own `beforeLoad`, so
 * it covers every tab (index, fleet-detail, reports, circularity) with one
 * check.
 */
export const Route = createFileRoute('/environmental')({
  beforeLoad: () => requireOrgFeature('sustainability'),
  component: EnvironmentalLayout,
});

const YEAR_OPTIONS = ['FY25', 'FY26', 'All'] as const;

function EnvironmentalLayout() {
  const [year, setYear] = useState<(typeof YEAR_OPTIONS)[number]>('All');
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // SIL-540/SIL-542, SIL-576: routes promoted to their own CUSTOMER_NAV
  // entry own their own PageHeader — this shared layout steps aside on
  // those paths.
  const hasOwnPageHeader = pathname === '/environmental/reports' || pathname === '/environmental/circularity';
  // SIL-612: Circularity has no member in this tab bar (SIL-576 promoted it
  // to its own nav entry), so the bar must not render there. Reports keeps
  // the bar — it's still a legitimate tab even though it also owns its own
  // PageHeader.
  const hasTabBar = pathname !== '/environmental/circularity';
  // SIL-611: the Regulatory-register cross-link renders at the TOP of every
  // environmental tab (moved out of EnvironmentalFooter so it's visible
  // without scrolling). Two exceptions:
  //   - Reports HOSTS the register; a page linking to an anchor on itself is noise.
  //   - Circularity owns its own PageHeader (SIL-576), so a layout-level link
  //     would render above that page's title. It renders the link itself,
  //     directly under its header, matching the other tabs.
  const hasRegulatoryCrossLink =
    pathname !== '/environmental/reports' && pathname !== '/environmental/circularity';

  return (
    <div className="space-y-4">
      {!hasOwnPageHeader && (
        <PageHeader
          title="Carbon footprint"
          pinTo="/environmental"
          subtitle="Emissions across the fleet, computed from measured and derived inputs. Sample-tagged widgets are labeled explicitly."
          actions={
            <div className="flex overflow-hidden rounded-md border border-zinc-300 bg-white text-xs font-semibold">
              {YEAR_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setYear(opt)}
                  className={`px-3 py-1.5 ${
                    year === opt ? 'bg-emerald-600 text-white' : 'text-zinc-500 hover:bg-zinc-100'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          }
        />
      )}

      {hasRegulatoryCrossLink && <RegulatoryRegisterLink />}

      {hasTabBar && <EnvironmentalTabBar />}

      <Outlet />

      <EnvironmentalFooter />
    </div>
  );
}
